import { z } from 'zod';
import { WithIdSchema } from './utils';

export const ActivableTranslationTransformSchema = <T extends z.ZodTypeAny>(schema: T) =>
  z.object({
    active: z.boolean(),
    timestamp: z.string(),
    modified: z.boolean().optional(),
    delta: schema,
  });

export const ActivableTranslationTransformsSchema = <T extends z.ZodTypeAny>(schema: T) =>
  z.record(ActivableTranslationTransformSchema(schema));

export const EventTemplateTranslatableSchema = z.object({
  title: z.string(),
  description: z.string(),
});
export const VenueTemplateTranslatableSchema = z.object({
  title: z.string(),
  description: z.string(),
});

export const ContentTemplateTranslatableSchema = z
  .object({
    name: z.string(),
    content: z.string(),
  })
  .merge(z.object({ description: z.string() }));

export const SegmentTranslatableSchema = z.object({
  name: z.string(),
});
export const LevelTranslatableSchema = z.object({
  name: z.string(),
});
export const AttributeTranslatableSchema = z.object({
  name: z.string(),
});
export const VenueTypeTranslatableSchema = z.object({
  name: z.string(),
});
export const MealPeriodTranslatableSchema = z.object({
  name: z.string(),
});
export const DayThemeTranslatableSchema = z.object({
  name: z.string(),
});
export const HighlightTranslatableSchema = z.object({
  name: z.string(),
});
export const CommonTextTranslatableSchema = z.object({
  name: z.string(),
});

export const TranslatableSchema = z.union([
  EventTemplateTranslatableSchema,
  VenueTemplateTranslatableSchema,
  ContentTemplateTranslatableSchema,
]);

export const EventTemplateTransformsSchema = ActivableTranslationTransformsSchema(
  EventTemplateTranslatableSchema
);
export const VenueTemplateTransformsSchema = ActivableTranslationTransformsSchema(
  VenueTemplateTranslatableSchema
);
export const ContentTemplateTransformsSchema = ActivableTranslationTransformsSchema(
  ContentTemplateTranslatableSchema
);
export const SegmentTransformsSchema =
  ActivableTranslationTransformsSchema(SegmentTranslatableSchema);
export const LevelTransformsSchema = ActivableTranslationTransformsSchema(LevelTranslatableSchema);
export const AttributeTransformsSchema = ActivableTranslationTransformsSchema(
  AttributeTranslatableSchema
);
export const VenueTypeTransformsSchema = ActivableTranslationTransformsSchema(
  VenueTypeTranslatableSchema
);
export const MealPeriodTransformsSchema = ActivableTranslationTransformsSchema(
  MealPeriodTranslatableSchema
);
export const DayThemeTransformsSchema = ActivableTranslationTransformsSchema(
  DayThemeTranslatableSchema
);
export const HighlightTransformsSchema = ActivableTranslationTransformsSchema(
  HighlightTranslatableSchema
);
export const CommonTextTransformsSchema = ActivableTranslationTransformsSchema(
  CommonTextTranslatableSchema
);

export const TranslationTransformsSchema = z.union([
  EventTemplateTransformsSchema,
  VenueTemplateTransformsSchema,
  ContentTemplateTransformsSchema,
  SegmentTransformsSchema,
  LevelTransformsSchema,
  AttributeTransformsSchema,
  VenueTypeTransformsSchema,
  MealPeriodTransformsSchema,
  DayThemeTransformsSchema,
  HighlightTransformsSchema,
  CommonTextTransformsSchema,
]);

export const TranslationBaseSchema = z.object({
  refers: z.string(),
});

export const EventTemplateTranslationBaseSchema = TranslationBaseSchema.extend({
  type: z.literal('eventtemplate'),
  transforms: EventTemplateTransformsSchema.optional(),
});

export const VenueTemplateTranslationBaseSchema = TranslationBaseSchema.extend({
  type: z.literal('venuetemplate'),
  transforms: VenueTemplateTransformsSchema.optional(),
});

export const ContentTemplateTranslationBaseSchema = TranslationBaseSchema.extend({
  type: z.literal('contenttemplate'),
  transforms: ContentTemplateTransformsSchema.optional(),
});

export const SegmentTranslationBaseSchema = TranslationBaseSchema.extend({
  type: z.literal('segment'),
  transforms: SegmentTransformsSchema.optional(),
});

export const LevelTranslationBaseSchema = TranslationBaseSchema.extend({
  type: z.literal('level'),
  transforms: LevelTransformsSchema.optional(),
});

export const AttributeTranslationBaseSchema = TranslationBaseSchema.extend({
  type: z.literal('attribute'),
  transforms: AttributeTransformsSchema.optional(),
});

export const VenueTypeTranslationBaseSchema = TranslationBaseSchema.extend({
  type: z.literal('venuetype'),
  transforms: VenueTypeTransformsSchema.optional(),
});

export const MealPeriodTranslationBaseSchema = TranslationBaseSchema.extend({
  type: z.literal('mealperiod'),
  transforms: MealPeriodTransformsSchema.optional(),
});

export const DayThemeTranslationBaseSchema = TranslationBaseSchema.extend({
  type: z.literal('daytheme'),
  transforms: DayThemeTransformsSchema.optional(),
});

export const HighlightTranslationBaseSchema = TranslationBaseSchema.extend({
  type: z.literal('highlight'),
  transforms: HighlightTransformsSchema.optional(),
});

export const CommonTextTranslationBaseSchema = TranslationBaseSchema.extend({
  type: z.literal('commonText'),
  transforms: CommonTextTransformsSchema.optional(),
});

export const EventTemplateTranslationSchema = TranslationBaseSchema.merge(
  EventTemplateTranslationBaseSchema
).merge(WithIdSchema);

export const VenueTemplateTranslationSchema = TranslationBaseSchema.merge(
  VenueTemplateTranslationBaseSchema
).merge(WithIdSchema);

export const ContentTemplateTranslationSchema = TranslationBaseSchema.merge(
  ContentTemplateTranslationBaseSchema
).merge(WithIdSchema);

export const SegmentTranslationSchema = TranslationBaseSchema.merge(
  SegmentTranslationBaseSchema
).merge(WithIdSchema);

export const LevelTranslationSchema = TranslationBaseSchema.merge(LevelTranslationBaseSchema).merge(
  WithIdSchema
);

export const AttributeTranslationSchema = TranslationBaseSchema.merge(
  AttributeTranslationBaseSchema
).merge(WithIdSchema);

export const VenueTypeTranslationSchema = TranslationBaseSchema.merge(
  VenueTypeTranslationBaseSchema
).merge(WithIdSchema);

export const MealPeriodTranslationSchema = TranslationBaseSchema.merge(
  MealPeriodTranslationBaseSchema
).merge(WithIdSchema);

export const DayThemeTranslationSchema = TranslationBaseSchema.merge(
  DayThemeTranslationBaseSchema
).merge(WithIdSchema);

export const HighlightTranslationSchema = TranslationBaseSchema.merge(
  HighlightTranslationBaseSchema
).merge(WithIdSchema);

export const CommonTextTranslationSchema = TranslationBaseSchema.merge(
  CommonTextTranslationBaseSchema
).merge(WithIdSchema);

export const TranslationSchema = z.union([
  EventTemplateTranslationSchema,
  VenueTemplateTranslationSchema,
  ContentTemplateTranslationSchema,
  SegmentTranslationSchema,
  LevelTranslationSchema,
  AttributeTranslationSchema,
  VenueTypeTranslationSchema,
  MealPeriodTranslationSchema,
  DayThemeTranslationSchema,
  HighlightTranslationSchema,
  CommonTextTranslationSchema,
]);

export const NewEventTemplateTranslationSchema = EventTemplateTranslationSchema.omit({ _id: true });
export const NewVenueTemplateTranslationSchema = VenueTemplateTranslationSchema.omit({ _id: true });
export const NewContentTemplateTranslationSchema = ContentTemplateTranslationSchema.omit({
  _id: true,
});
export const NewSegmentTranslationSchema = SegmentTranslationSchema.omit({ _id: true });
export const NewLevelTranslationSchema = LevelTranslationSchema.omit({ _id: true });
export const NewAttributeTranslationSchema = AttributeTranslationSchema.omit({ _id: true });
export const NewVenueTypeTranslationSchema = VenueTypeTranslationSchema.omit({ _id: true });
export const NewMealPeriodTranslationSchema = MealPeriodTranslationSchema.omit({ _id: true });
export const NewDayThemeTranslationSchema = DayThemeTranslationSchema.omit({ _id: true });
export const NewHighlightTranslationSchema = HighlightTranslationSchema.omit({ _id: true });
export const NewCommonTextTranslationSchema = CommonTextTranslationSchema.omit({ _id: true });

export const TranslatedSchema = <T extends z.ZodTypeAny>(schema: T) =>
  schema.and(
    z.object({
      _lang: z.string(),
    })
  );

export const AutoResolveConflictStrategySchema = z.literal('mine');

export const AutoResolveConflictSchema = z.object({
  path: z.string(),
  strategy: AutoResolveConflictStrategySchema,
});
